import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  CircleNotch,
  FloppyDisk,
  ClockCounterClockwise,
  WarningCircle,
  ArrowClockwise,
  Check,
  FileText,
  Target,
  Lightbulb,
} from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";
import ChallengeMap, { MAP_SECTIONS } from "@/components/ChallengeMap";
import { ThinkingTimer } from "@/components/ThinkingTimer";
import ExecutiveSummary from "@/components/ExecutiveSummary";
import ProblemConsole from "@/components/ProblemConsole";
import AreasOfNeed from "@/components/AreasOfNeed";
import CommandBar from "@/components/CommandBar";
import SolutionSpace from "@/components/SolutionSpace";

const AnalysisScreen = ({ revising = false }) => (
  <div data-testid="analysis-screen" className="mx-auto max-w-2xl px-6 py-24">
    <div className="relative overflow-hidden border border-border bg-card p-10">
      <div className="scanline pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-foreground/5 to-transparent" />
      <div className="overline mb-4">{revising ? "Updating" : "Reading your challenge"}</div>
      <h2 className="font-serif text-3xl leading-tight caret">
        {revising ? "Folding in your changes" : "Mapping the problem"}
      </h2>
      <p className="mt-5 max-w-prose text-[15px] leading-relaxed text-muted-foreground">
        {revising
          ? "Re-reading your challenge with the corrections and evidence you added, then updating the picture."
          : "Reading what you wrote — the facts, the assumptions, and what might be missing — to lay out a clear, shared picture of the problem."}
      </p>
      <div className="mt-8 flex items-center gap-2 text-sm text-muted-foreground">
        <CircleNotch size={16} className="animate-spin" />
        {revising ? "Updating…" : "Mapping the problem…"}
      </div>
      <ThinkingTimer className="mt-4" />
    </div>
  </div>
);

const UnansweredBanner = ({ count, onAnswer }) => (
  <div data-testid="unanswered-banner" className="w-full px-8 pt-5">
    <div className="flex flex-col gap-2 rounded-sm border border-amber-500/50 bg-amber-500/10 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <WarningCircle size={18} className="mt-0.5 shrink-0 text-amber-600" weight="fill" />
        <p className="text-sm leading-relaxed text-foreground/90">
          <strong className="font-medium">{count} unanswered question{count > 1 ? "s" : ""}.</strong>{" "}
          A complete representation and solution can't be built until these are addressed.
        </p>
      </div>
      <button
        data-testid="answer-questions-btn"
        onClick={onAnswer}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-sm border border-primary bg-primary px-3.5 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/85"
      >
        <FileText size={13} weight="bold" /> Answer them
      </button>
    </div>
  </div>
);

const LeftNav = ({ active }) => (
  <nav className="thin-scroll sticky top-20 hidden max-h-[calc(100vh-6rem)] overflow-y-auto lg:block">
    <div className="overline mb-4">The full analysis</div>
    <ol className="space-y-1">
      {MAP_SECTIONS.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            data-testid={`nav-${s.id}`}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className={`flex items-baseline gap-2 border-l-2 py-1.5 pl-3 text-sm transition-colors ${
              active === s.id
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
            }`}
          >
            <span className="font-mono text-[10px]">{s.n}</span>
            <span className="leading-snug">{s.title}</span>
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export default function SessionView({ demoId, renderTour }) {
  const params = useParams();
  const id = demoId || params.id;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [active, setActive] = useState(MAP_SECTIONS[0].id);
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(
    searchParams.get("tab") === "areas" ? "areas"
      : searchParams.get("tab") === "solutions" ? "solutions"
      : "map"
  );
  const [showFullMap, setShowFullMap] = useState(false);
  const auditStarted = useRef(false);
  const areasStarted = useRef(false);

  const load = useCallback(async () => {
    const res = await api.getSession(id);
    setData(res);
    const v = await api.getVersions(id);
    setVersions(v);
    return res;
  }, [id]);

  const runAudit = useCallback(async () => {
    setError(null);
    try {
      await api.runAudit(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [id, load]);

  // initial load + kick off audit if needed
  useEffect(() => {
    (async () => {
      const res = await load();
      if (!res.representation && !res.session.processing && !res.session.process_error && !auditStarted.current) {
        auditStarted.current = true;
        runAudit();
      }
    })().catch((e) => setError(e.message));
  }, [id, load, runAudit]);

  // Build the Problem Space on demand (user clicks "Continue to Problem Space")
  const goToAreas = useCallback(() => {
    setView("areas");
    if (
      data?.representation &&
      !data?.areas_of_need?.areas?.length &&
      !data?.session?.areas_processing &&
      !data?.session?.processing &&
      !areasStarted.current
    ) {
      areasStarted.current = true;
      api.buildAreasOfNeed(id).then(load).catch(() => {});
    }
  }, [data, id, load]);

  // poll while the backend is processing (audit/revision OR problem-space/solutions/plan build)
  useEffect(() => {
    const busy = data?.session?.processing || data?.session?.areas_processing || data?.session?.need_solutions_processing || data?.session?.plan_processing;
    if (!busy) return;
    const t = setInterval(() => {
      load().catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [data?.session?.processing, data?.session?.areas_processing, data?.session?.need_solutions_processing, data?.session?.plan_processing, load]);

  // scroll to top on tab change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [view]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActive(en.target.id);
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );
    MAP_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [data]);

  const processing = data?.session?.processing;
  const processKind = data?.session?.process_kind;
  const currentRep = data?.representation;
  const selectedVersion = selectedVersionId
    ? versions.find((v) => v.id === selectedVersionId)
    : null;
  const displayedRep = selectedVersion || currentRep;
  const displayedMap = displayedRep?.map;
  const unansweredCount = Math.min((currentRep?.map?.missing_or_underrepresented?.length || 0), 5);

  const saveCurrent = async () => {
    if (!displayedRep) return;
    try {
      await api.markCurrent(id, displayedRep.id);
      toast.success("Saved", {
        description: `This version is now the current one.`,
      });
      setSelectedVersionId(null);
      await load();
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    }
  };

  const provideClarity = async (instruction, answered) => {
    try {
      await api.revise(id, instruction, answered);
      toast.success("Updating the analysis", { description: "Folding in your information — a new version will appear shortly." });
      await load();
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message });
    }
  };

  const backendError = error || data?.session?.process_error;

  if (!data) return <AnalysisScreen />;
  if (processing) return <AnalysisScreen revising={processKind === "revise"} />;

  // No representation exists at all — show the full error / retry screen.
  if (!displayedMap) {
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <WarningCircle size={32} className="mx-auto mb-4" style={{ color: "hsl(var(--destructive))" }} />
        <h2 className="font-serif text-2xl">Examination could not complete</h2>
        <p className="mt-2 text-sm text-muted-foreground">{String(backendError || "No representation was produced.")}</p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            data-testid="retry-audit-btn"
            onClick={() => { auditStarted.current = true; runAudit(); }}
            className="inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/85"
          >
            <ArrowClockwise size={15} /> Retry examination
          </button>
          <Link to="/" className="inline-flex items-center rounded-sm border border-border px-4 py-2 text-sm hover:bg-muted">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Persistent header — consolidated */}
      <div className="sticky top-16 z-30 border-b border-border bg-background/90 backdrop-blur-md">
        <div className="w-full px-8 py-2.5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2">
              {[
                { key: "map", n: 1, label: "The Problem", Icon: FileText, testid: "tab-representation", onClick: () => setView("map"), done: !!currentRep },
                { key: "areas", n: 2, label: "Problem Space", Icon: Target, testid: "tab-areas", onClick: goToAreas, done: !!data?.areas_of_need?.areas?.length },
                { key: "solutions", n: 3, label: "Strategies", Icon: Lightbulb, testid: "tab-solutions", onClick: () => setView("solutions"), done: !!(data?.systems_map?.nodes?.length || data?.solution_systems?.length) },
              ].map((s, i) => {
                const activeStep = view === s.key;
                return (
                  <div key={s.key} className="flex items-center">
                    {i > 0 && <span className="mx-1 h-px w-4 shrink-0 bg-border sm:w-6" />}
                    <button
                      data-testid={s.testid}
                      onClick={s.onClick}
                      className={`inline-flex items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm transition-colors ${
                        activeStep ? "bg-foreground text-background font-medium" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <span
                        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold ${
                          activeStep
                            ? "bg-background text-foreground"
                            : s.done
                            ? "bg-[hsl(var(--tag-preference))] text-white"
                            : "border border-current text-muted-foreground"
                        }`}
                      >
                        {s.done && !activeStep ? <Check size={12} weight="bold" /> : s.n}
                      </span>
                      <span className="hidden sm:inline">{s.label}</span>
                    </button>
                  </div>
                );
              })}
            </div>
            {view === "map" && (
              <div className="flex shrink-0 items-center gap-2">
                <button
                  data-testid="version-history-btn"
                  onClick={() => setShowHistory((s) => !s)}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
                >
                  <ClockCounterClockwise size={14} /> History ({versions.length})
                </button>
                <button
                  data-testid="save-current-btn"
                  onClick={saveCurrent}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-primary bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/85"
                >
                  <FloppyDisk size={14} /> Save
                </button>
              </div>
            )}
          </div>

          {showHistory && (
            <div data-testid="version-history-panel" className="reveal mt-4 border-t border-border pt-4">
              <div className="flex flex-wrap gap-2">
                {versions.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`version-${v.version_number}`}
                    onClick={() => setSelectedVersionId(v.is_current ? null : v.id)}
                    className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-xs transition-colors ${
                      displayedRep.id === v.id ? "border-foreground" : "border-border hover:bg-muted"
                    }`}
                  >
                    <span className="font-mono">v{v.version_number}</span>
                    <span className="text-muted-foreground">
                      {v.source === "initial_audit" ? "original" : "updated"}
                    </span>
                    <span className="text-muted-foreground">· {timeAgo(v.created_at)}</span>
                    {v.is_current && <Check size={13} weight="bold" style={{ color: "hsl(var(--tag-preference))" }} />}
                  </button>
                ))}
              </div>
              {selectedVersionId && (
                <p className="mt-2 text-xs text-muted-foreground">
                  You're viewing an earlier version. Use “Save” to make it the current one.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      {view === "map" && (
        <>
          {backendError && (
            <div data-testid="revision-error-banner" className="w-full px-8 pt-4">
              <div className="flex items-start gap-3 border border-destructive/40 bg-destructive/5 px-4 py-3">
                <WarningCircle size={18} className="mt-0.5 shrink-0" style={{ color: "hsl(var(--destructive))" }} />
                <div className="flex-1 text-sm">
                  <p className="font-medium">The last revision did not complete.</p>
                  <p className="mt-0.5 text-muted-foreground">{String(backendError)} — your current representation below is unchanged.</p>
                </div>
              </div>
            </div>
          )}
          {showFullMap ? (
            <div className="grid w-full grid-cols-1 gap-8 px-8 py-8 lg:grid-cols-[220px_1fr]">
              <LeftNav active={active} />
              <main className="min-w-0">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <button
                    data-testid="back-to-summary-btn"
                    onClick={() => setShowFullMap(false)}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"
                  >
                    <ArrowClockwise size={14} /> Back to summary
                  </button>
                  <span className="overline">Complete Challenge Map</span>
                </div>
                <div className="mb-4 border border-border bg-muted px-5 py-4">
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    This is a <strong className="text-foreground">shared working picture</strong> of the problem — a starting point, not the final word.
                    Everything here can be expanded, corrected, or reframed by you.
                  </p>
                </div>
                <ChallengeMap map={displayedMap} ceoEvidence={data.evidence} />
              </main>
            </div>
          ) : (
            <div className="px-6 pb-28 pt-6">
              <ExecutiveSummary
                map={displayedMap}
                onViewFull={() => setShowFullMap(true)}
                onProvideClarity={provideClarity}
                onContinue={goToAreas}
                unansweredCount={unansweredCount}
                updating={processing}
              />
            </div>
          )}
          <ProblemConsole sessionId={id} onChanged={load} />
        </>
      )}

      {view === "areas" && (
        <>
          {!demoId && unansweredCount > 0 && <UnansweredBanner count={unansweredCount} onAnswer={() => setView("map")} />}
          <AreasOfNeed
            sessionId={id}
            data={data.areas_of_need}
            currentVersionId={data.session?.current_version_id}
            processing={data.session?.areas_processing}
            error={data.session?.areas_error}
            onChanged={load}
            onNext={() => setView("solutions")}
          />
        </>
      )}

      {view === "solutions" && (
        <>
          {!demoId && unansweredCount > 0 && <UnansweredBanner count={unansweredCount} onAnswer={() => setView("map")} />}
          <SolutionSpace
            sessionId={id}
            problemTitle={data.session?.title || displayedMap?.current_challenge_formulation}
            areasOfNeed={data.areas_of_need}
            solutionSystems={data.solution_systems}
            systemsMap={data.systems_map}
            causalModel={data.causal_model}
            plan={data.action_plan}
            planProcessing={data.session?.plan_processing}
            processing={data.session?.need_solutions_processing}
            error={data.session?.need_solutions_error}
            onChanged={load}
          />
        </>
      )}

      {renderTour?.({ view, setView })}

      {(view === "areas" || view === "solutions") && data.areas_of_need?.areas?.length > 0 && (
        <CommandBar sessionId={id} onChanged={load} />
      )}
    </div>
  );
}
